/* eslint-disable no-else-return */
import { createSelector } from 'reselect';
import { visibleAccountsSelector } from './AccountsStepSelector';
import { isNonEmptyString } from '../../../../../js/app/react/validators';
import { workflowId as workflowIdSelector } from '../../../AdvWorkflow/AdvWorkflowSelector';

import { WORKFLOW_ID as RETIREMENT_WORKFLOW_ID } from '../../../RetirementWorkflow/RetirementWorkflowConstants';
import { WORKFLOW_ID as EDUCATION_WORKFLOW_ID } from '../../../EducationWorkflow/EducationWorkflowConstants';
import { WORKFLOW_ID as HOME_PURCHASE_WORKFLOW_ID } from '../../../HomePurchaseWorkflow/HomePurchaseWorkflowConstants';
import { WORKFLOW_ID as EMERGENCY_FUND_WORKFLOW_ID } from '../../../EmergencyFundWorkflow/EmergencyFundWorkflowConstants';
import { WORKFLOW_ID as LARGE_PURCHASE_WORKFLOW_ID } from '../../../LargePurchaseWorkflow/LargePurchaseWorkflowConstants';
import { WORKFLOW_ID as HEALTHCARE_WORKFLOW_ID } from '../../../HealthcareWorkflow/HealthcareWorkflowConstants';
import { WORKFLOW_ID as DECUMULATION_WORKFLOW_ID } from '../../../DecumulationWorkflow/DecumulationWorkflow.constants';
import {
  hasEducationWorkflowData,
  getSelectedChildFirstName,
} from '../../../ChildrenInfoWorkflow/ChildrenInfoWorkflowSelector';
import largePurchaseItemNameSelector from '../../../LargePurchaseWorkflow/Steps/ItemDescription/ItemDescriptionSelector';
import { hasLargePurchaseWorkflowData } from '../../../LargePurchaseWorkflow/LargePurchaseWorkflowSelector';
import { hasHomePurchaseWorkflowData } from '../../../HomePurchaseWorkflow/HomePurchaseWorkflowSelector';
import { nullablePropertyNameSelector } from '../../../HomePurchaseWorkflow/Steps/PropertyName/PropertyNameSelector';

export const composeLabel = (description, accountName) => {
  if (isNonEmptyString(accountName)) {
    return accountName;
  }
  if (isNonEmptyString(description)) {
    return description;
  }
  return 'Untitled';
};
export const formatMoney = number => `$${window.ADV.util.number_format(number, 0, '.', ',')}`;

export const getAccountEarmarkType = (workflowId, name) => {
  if (workflowId === RETIREMENT_WORKFLOW_ID) {
    return 'Retirement';
  } else if (workflowId === EDUCATION_WORKFLOW_ID) {
    return `Education ${name}`;
  } else if (workflowId === HOME_PURCHASE_WORKFLOW_ID) {
    return `HomePurchase ${name}`;
  } else if (workflowId === EMERGENCY_FUND_WORKFLOW_ID) {
    return 'EmergencyFund';
  } else if (workflowId === LARGE_PURCHASE_WORKFLOW_ID) {
    return `LargePurchase ${name}`;
  } else if (workflowId === HEALTHCARE_WORKFLOW_ID) {
    return 'HealthCare';
  } else if (workflowId === DECUMULATION_WORKFLOW_ID) {
    return 'Decumulation';
  }
  throw new Error('getAccountEarmarkType unknown workflow ID');
};

export const getAccountEarmarkTooltip = (workflowId, name) => {
  if (workflowId === RETIREMENT_WORKFLOW_ID) {
    return 'Retirement';
  } else if (workflowId === EDUCATION_WORKFLOW_ID) {
    return `${name}'s Education`;
  } else if (workflowId === HOME_PURCHASE_WORKFLOW_ID) {
    return name;
  } else if (workflowId === EMERGENCY_FUND_WORKFLOW_ID) {
    return 'Emergency Fund';
  } else if (workflowId === LARGE_PURCHASE_WORKFLOW_ID) {
    return `Purchase ${name}`;
  } else if (workflowId === HEALTHCARE_WORKFLOW_ID) {
    return 'Health Care';
  } else if (workflowId === DECUMULATION_WORKFLOW_ID) {
    return 'Decumulation';
  }
  throw new Error('getAccountEarmarkTooltip unknown workflow ID');
};

export const getAccountsTitle = (workflowId, name) => {
  if (workflowId === HEALTHCARE_WORKFLOW_ID) {
    return 'Which accounts will fund your health care costs?';
  }

  if (workflowId === EDUCATION_WORKFLOW_ID) {
    return `Which accounts will fund ${name}'s \n education goal?`;
  }
  if (
    workflowId === HOME_PURCHASE_WORKFLOW_ID ||
    workflowId === EMERGENCY_FUND_WORKFLOW_ID ||
    workflowId === LARGE_PURCHASE_WORKFLOW_ID ||
    workflowId === DECUMULATION_WORKFLOW_ID
  ) {
    return 'Which accounts will fund this goal?';
  }
  return 'Which accounts will fund your retirement?';
};

export const isEarmarked = (AccountEarmark, workflowId, name = null) =>
  AccountEarmark.some(
    earmark =>
      earmark.Type === getAccountEarmarkType(workflowId, name) ||
      (workflowId !== DECUMULATION_WORKFLOW_ID && earmark.Type === 'AllGoals'),
  );

export const selectedNameSelector = (state, ownProps) => {
  if (hasEducationWorkflowData(state, ownProps)) {
    return getSelectedChildFirstName(state, ownProps);
  }

  if (hasLargePurchaseWorkflowData(state, ownProps)) {
    return largePurchaseItemNameSelector(state, ownProps);
  }

  if (hasHomePurchaseWorkflowData(state, ownProps)) {
    return nullablePropertyNameSelector(state, ownProps);
  }
  return '';
};

export const accountsOverviewSelector = createSelector(
  workflowIdSelector,
  visibleAccountsSelector,
  selectedNameSelector,
  (workflowId, accounts, selectedName) =>
    accounts
      .filter(({ ShowAccount }) => ShowAccount)
      .map(
        ({
          AccountId,
          Description,
          AccountName,
          AccountEarmark,
          frontEndId,
          CashBalance,
          IsLinked,
        }) => ({
          id: AccountId || frontEndId,
          label: composeLabel(Description, AccountName),
          value: formatMoney(CashBalance),
          isGhost: false,
          isLink: IsLinked,
          isSelected: isEarmarked(AccountEarmark, workflowId, selectedName),
        }),
      ),
);

export const isEmptyStateSelector = createSelector(
  accountsOverviewSelector,
  accountsOverview => accountsOverview.length === 0,
);
